import { Button, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import React from 'react';
import { PricingAPI } from '../../../../../controller/PricingAPI';
import TodoTemplateFormModal from './TodoTemplateFormModal';
import { formatStartTime, recurrenceLabel, showRequestError } from '../todoHelpers';

// 例行範本定義「誰要定期做什麼」。只有管理員能增修，其他人只看得到
// 自己與自己部門的 —— 範圍由後端縮，這裡只依 can.* 決定按鈕。
function TodoRoutinePane({ canManage, onCompliance }) {
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [form, setForm] = React.useState(null);

    const refresh = React.useCallback(() => {
        setLoading(true);
        PricingAPI.todoTemplates.index()
            .then((response) => setRows(response.data.data || []))
            .catch(showRequestError)
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        refresh();
    }, [refresh]);

    const handleToggleActive = (row) => {
        // 停用只改一個欄位，但後端的 PATCH 是整份覆寫，所以要把其他欄位一起送回去
        PricingAPI.todoTemplates.update(row.id, {
            subject: row.subject,
            description: row.description,
            assignee_id: row.assignee_id,
            department_id: row.department_id,
            recurrence: row.recurrence,
            recurrence_days: row.recurrence_days || [],
            start_time: formatStartTime(row.start_time),
            effective_from: row.effective_from,
            active: !row.active,
            checklist: row.checklist || [],
        }).then(() => {
            message.success(row.active ? '已停用' : '已啟用');
            refresh();
        }).catch((error) => {
            // 失敗也要重抓：範本可能已被別人刪掉或改動，不重抓會留著殘影
            showRequestError(error);
            refresh();
        });
    };

    const handleDelete = (row) => {
        PricingAPI.todoTemplates.destroy(row.id)
            .then(() => {
                message.success('已刪除');
                refresh();
            })
            .catch((error) => {
                showRequestError(error);
                refresh();
            });
    };

    const columns = [
        { title: '主旨', dataIndex: 'subject', width: 240 },
        { title: '重複規則', width: 200, render: (_, row) => recurrenceLabel(row) },
        {
            title: '負責人',
            width: 120,
            render: (_, row) => (row.assignee ? row.assignee.name : <Typography.Text type="secondary">—</Typography.Text>),
        },
        {
            title: '部門',
            width: 120,
            render: (_, row) => (row.department ? <Tag color="blue">{row.department.name}</Tag> : <Typography.Text type="secondary">—</Typography.Text>),
        },
        {
            title: '時間',
            width: 90,
            render: (_, row) => <span style={{ whiteSpace: 'nowrap' }}>{formatStartTime(row.start_time) || '整天'}</span>,
        },
        {
            title: '檢查清單',
            width: 100,
            align: 'right',
            render: (_, row) => <span style={{ whiteSpace: 'nowrap' }}>{row.checklist && row.checklist.length ? `${row.checklist.length} 項` : '—'}</span>,
        },
        {
            title: '生效起日',
            dataIndex: 'effective_from',
            width: 110,
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{value}</span>,
        },
        {
            title: '狀態',
            dataIndex: 'active',
            width: 80,
            render: (active) => (active ? <Tag color="green">啟用</Tag> : <Tag>停用</Tag>),
        },
        {
            title: '操作',
            width: 230,
            render: (_, row) => {
                const can = row.can || {};
                if (!can.update && !can.delete) {
                    return <Typography.Text type="secondary">—</Typography.Text>;
                }
                return (
                    <Space>
                        {can.update && <Typography.Link onClick={() => setForm({ template: row })}>修改</Typography.Link>}
                        {can.update && (
                            <Typography.Link onClick={() => handleToggleActive(row)}>
                                {row.active ? '停用' : '啟用'}
                            </Typography.Link>
                        )}
                        {can.delete && (
                            <Popconfirm
                                title="確定要刪除這個例行工作嗎?"
                                okText="刪除"
                                cancelText="取消"
                                onConfirm={() => handleDelete(row)}
                            >
                                <Typography.Link type="danger">刪除</Typography.Link>
                            </Popconfirm>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 12 }} wrap>
                {canManage && (
                    <Button type="primary" onClick={() => setForm({ template: null })}>新增例行工作</Button>
                )}
                <Button onClick={onCompliance}>合規檢視</Button>
            </Space>
            <Typography.Paragraph type="secondary">
                例行工作會自動出現在負責人的「待我處理」，按「開始」才會變成一筆待辦。日期以 Google 行事曆為準：要改期先改行事曆，再改這裡。
            </Typography.Paragraph>
            <Table
                rowKey="id"
                loading={loading}
                dataSource={rows}
                columns={columns}
                pagination={false}
                scroll={{ x: 1290 }}
            />
            <TodoTemplateFormModal
                open={!!form}
                template={form ? form.template : null}
                onClose={() => setForm(null)}
                onSaved={() => { setForm(null); refresh(); }}
            />
        </div>
    );
}

export default TodoRoutinePane;
